'use client';

import { useState, type CSSProperties, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { AccountError, PortalLink, type CommerceSource } from '@akademi/site-builder-runtime';
import {
  AccountField,
  NEW_PASSWORD_MIN_LENGTH,
  buttonStyle,
  errorStyle,
  formStyle,
  inputStyle,
  mutedStyle,
  noticeStyle,
  secondaryButtonStyle,
  signInHrefFor,
  useAccountErrorText,
} from './accountShared';
import { newPasswordProblem, type NewPasswordProblem } from './registration';
import {
  NO_TRADE_REQUIREMENTS,
  type TradeApplicationStatus,
  type TradeCommerceSource,
  type TradeDocumentType,
  type TradeDocumentUpload,
  type TradeRequirements,
} from './tradeApplication';
import {
  CERTIFICATE_ACCEPT,
  MAX_CERTIFICATE_BYTES,
  refusalMessage,
  type InstallerApplicationStatus,
  type InstallerCommerceSource,
  type InstallerPolicyType,
} from './installer';

/**
 * Applying in the same step as making the account (contract §7): the account's fields sit at the
 * top of the trade or installer form, and the account and the application go to the shop together
 * through its sign-up. A refusal is the shop's own sentence, and no account is made.
 *
 * <p>A sign-up cannot carry files, so what follows is: the emailed code when the shop confirms
 * addresses (the password is still in hand, so only the code is asked for), then — signed in — the
 * documents or certificates the application still lacks, uploaded to the shop's own endpoints.
 * The same upload panels serve the account page, where anything still outstanding is shown.
 */

// ---- the account's fields -------------------------------------------------------------------

export interface GuestAccountDraft {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
}

export const EMPTY_GUEST_ACCOUNT: GuestAccountDraft = {
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  confirmPassword: '',
};

export type GuestAccountProblem = 'MISSING' | 'INVALID_EMAIL' | NewPasswordProblem;

const EMAIL = /^[^@\s\\]+@[^@\s\\]+$/;

/** What is missing or wrong with the account's fields; the shop checks them all again. */
export function guestAccountProblem(draft: GuestAccountDraft): GuestAccountProblem | null {
  if (!draft.firstName.trim() || !draft.lastName.trim() || !draft.email.trim() || !draft.password) return 'MISSING';
  if (!EMAIL.test(draft.email.trim())) return 'INVALID_EMAIL';
  return newPasswordProblem(draft.password, draft.confirmPassword);
}

/** The problem in words. */
export function useGuestAccountProblemText(): (problem: GuestAccountProblem) => string {
  const { t } = useTranslation('site-builder-blocks');
  return (problem) =>
    problem === 'MISSING'
      ? t('commerce.signUpApply.account.missing')
      : problem === 'INVALID_EMAIL'
        ? t('commerce.signUpApply.account.invalidEmail')
        : t(`commerce.account.errors.${problem}`);
}

/**
 * A sign-up that carried an application, refused: the shop's own sentence when it wrote one for
 * the applicant (`APPLICATION_REFUSED`), else the account's own words for the refusal.
 */
export function useSignUpRefusalText(): (failure: unknown) => string {
  const accountText = useAccountErrorText();
  return (failure) => {
    if (failure instanceof AccountError && (failure.kind as string) === 'APPLICATION_REFUSED') {
      return refusalMessage(failure) ?? accountText(new AccountError('INVALID_INPUT', 400));
    }
    return accountText(failure);
  };
}

/** The account's fields, as the first section of an application form, with the way to sign in instead. */
export function GuestAccountFields({
  value,
  onChange,
  idPrefix,
  signInHref,
  here,
}: {
  value: GuestAccountDraft;
  onChange: (value: GuestAccountDraft) => void;
  idPrefix: string;
  signInHref: string;
  here: string | null;
}) {
  const { t } = useTranslation('site-builder-blocks');
  const set = (field: keyof GuestAccountDraft) => (text: string) => onChange({ ...value, [field]: text });
  return (
    <fieldset style={sectionStyle}>
      <legend style={legendStyle}>{t('commerce.signUpApply.account.title')}</legend>
      <p style={{ ...mutedStyle, margin: 0, fontSize: 14 }}>
        {t('commerce.signUpApply.account.lead')}{' '}
        <PortalLink href={signInHrefFor(signInHref, here)}>{t('commerce.signUpApply.account.signIn')}</PortalLink>
      </p>
      <div style={pairStyle}>
        <AccountField
          id={`${idPrefix}-account-first-name`}
          label={t('commerce.account.firstName')}
          value={value.firstName}
          onChange={set('firstName')}
          autoComplete="given-name"
          required
          maxLength={100}
        />
        <AccountField
          id={`${idPrefix}-account-last-name`}
          label={t('commerce.account.lastName')}
          value={value.lastName}
          onChange={set('lastName')}
          autoComplete="family-name"
          required
          maxLength={100}
        />
      </div>
      <AccountField
        id={`${idPrefix}-account-email`}
        label={t('commerce.account.email')}
        value={value.email}
        onChange={set('email')}
        type="email"
        autoComplete="email"
        required
        maxLength={254}
        hint={t('commerce.signUpApply.account.emailHint')}
      />
      <AccountField
        id={`${idPrefix}-account-password`}
        label={t('commerce.account.password')}
        value={value.password}
        onChange={set('password')}
        type="password"
        autoComplete="new-password"
        required
        hint={t('commerce.account.passwordRuleHint', { count: NEW_PASSWORD_MIN_LENGTH })}
      />
      <AccountField
        id={`${idPrefix}-account-confirm-password`}
        label={t('commerce.account.confirmPassword')}
        value={value.confirmPassword}
        onChange={set('confirmPassword')}
        type="password"
        autoComplete="new-password"
        required
      />
    </fieldset>
  );
}

// ---- the emailed code -----------------------------------------------------------------------

/**
 * The code the shop emailed, for a shop that confirms addresses. The password was chosen on the
 * form, so only the code is asked for; once it is taken the shopper is signed in and `onSignedIn`
 * runs. A shop that opens the account without signing in says so, with the way to sign in.
 */
export function ActivationCodeStep({
  commerce,
  email,
  password,
  idPrefix,
  signInHref,
  here,
  onSignedIn,
}: {
  commerce: CommerceSource;
  email: string;
  password: string;
  idPrefix: string;
  signInHref: string;
  here: string | null;
  onSignedIn: () => void | Promise<void>;
}) {
  const { t } = useTranslation('site-builder-blocks');
  const errorText = useAccountErrorText();
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [codeRefused, setCodeRefused] = useState(false);
  const [resent, setResent] = useState(false);
  const [readyNotSignedIn, setReadyNotSignedIn] = useState(false);

  async function confirm(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!commerce.activateAccount || busy) return;
    if (!code.trim()) {
      setError(t('commerce.signUpApply.activate.missing'));
      return;
    }
    setBusy(true);
    setError(null);
    setCodeRefused(false);
    setResent(false);
    try {
      const profile = await commerce.activateAccount({ email, code: code.trim(), password });
      if (profile) {
        await onSignedIn();
        return;
      }
      setReadyNotSignedIn(true);
    } catch (failure) {
      setCodeRefused(failure instanceof AccountError && failure.kind === 'INVALID_ACTIVATION');
      setError(errorText(failure));
    }
    setBusy(false);
  }

  async function resend() {
    if (!commerce.resendActivation || busy) return;
    setBusy(true);
    setError(null);
    try {
      await commerce.resendActivation(email);
      setResent(true);
      setCodeRefused(false);
    } catch (failure) {
      setError(errorText(failure));
    } finally {
      setBusy(false);
    }
  }

  if (readyNotSignedIn || !commerce.activateAccount) {
    return (
      <div style={{ display: 'grid', gap: 12 }}>
        <p role="status" style={noticeStyle}>
          {readyNotSignedIn
            ? t('commerce.signUpApply.activate.readySignIn')
            : t('commerce.signUpApply.activate.checkEmail', { email })}
        </p>
        <PortalLink href={signInHrefFor(signInHref, here)}>{t('commerce.account.signIn')}</PortalLink>
      </div>
    );
  }

  return (
    <form onSubmit={(event) => void confirm(event)} noValidate style={formStyle}>
      <h2 style={legendStyle}>{t('commerce.signUpApply.activate.title')}</h2>
      <p role="status" style={noticeStyle}>
        {t('commerce.signUpApply.activate.lead', { email })}
      </p>
      {error && (
        <p role="alert" style={errorStyle}>
          {error}
        </p>
      )}
      {resent && (
        <p role="status" style={noticeStyle}>
          {t('commerce.account.activationResent')}
        </p>
      )}
      {codeRefused && commerce.resendActivation && (
        <button type="button" disabled={busy} onClick={() => void resend()} style={secondaryButtonStyle}>
          {busy ? t('commerce.account.sending') : t('commerce.account.resendActivation')}
        </button>
      )}
      <AccountField
        id={`${idPrefix}-activation-code`}
        label={t('commerce.account.emailedCode')}
        value={code}
        onChange={setCode}
        autoComplete="one-time-code"
        required
        maxLength={64}
      />
      <button type="submit" disabled={busy} style={buttonStyle}>
        {busy ? t('commerce.signUpApply.activate.submitting') : t('commerce.signUpApply.activate.submit')}
      </button>
    </form>
  );
}

// ---- the files that follow ------------------------------------------------------------------

const ACCEPT = '.pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png';

type DocumentRow = { key: number; type: TradeDocumentType | ''; file: File | null };

/**
 * The proof documents a pending trade application still lacks, uploaded as the signed-in shopper.
 * Rows of what each file proves and the file, as on the form; the ones already chosen there come
 * in as `initial`. `outstanding` says the shop is waiting for them; otherwise they are the
 * shopper's own choice, and can be skipped.
 */
export function TradeDocumentsUpload({
  commerce,
  requirements = NO_TRADE_REQUIREMENTS,
  initial = [],
  outstanding,
  onDone,
  onSkip,
}: {
  commerce: TradeCommerceSource;
  requirements?: TradeRequirements;
  initial?: TradeDocumentUpload[];
  outstanding: boolean;
  onDone: (status: TradeApplicationStatus) => void;
  onSkip?: () => void;
}) {
  const { t } = useTranslation('site-builder-blocks');
  const [rows, setRows] = useState<DocumentRow[]>(() =>
    initial.length
      ? initial.map((document, key) => ({ key, type: document.type, file: document.file }))
      : [{ key: 0, type: '', file: null }],
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const chosen: TradeDocumentUpload[] = rows
    .filter((row): row is DocumentRow & { type: TradeDocumentType; file: File } => !!row.type && !!row.file)
    .map((row) => ({ type: row.type, file: row.file }));

  async function upload(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!commerce.uploadTradeDocuments || busy) return;
    if (rows.some((row) => row.file && !row.type)) {
      setError(t('commerce.trade.problems.documentType'));
      return;
    }
    if (!chosen.length) {
      setError(t('commerce.signUpApply.documents.none'));
      return;
    }
    const large = chosen.find((document) => document.file.size > requirements.maxBytes);
    if (large) {
      setError(t('commerce.trade.problems.fileTooLarge', { name: large.file.name, size: megabytes(requirements.maxBytes) }));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      onDone(await commerce.uploadTradeDocuments(chosen));
    } catch (failure) {
      setError(refusalMessage(failure) ?? t(`commerce.trade.errors.${uploadErrorKind(failure)}`));
      setBusy(false);
    }
  }

  return (
    <form onSubmit={(event) => void upload(event)} noValidate style={{ ...formStyle, maxWidth: 560 }}>
      <h2 style={legendStyle}>{t('commerce.signUpApply.documents.title')}</h2>
      <p style={{ ...mutedStyle, margin: 0 }}>
        {t(outstanding ? 'commerce.signUpApply.documents.lead' : 'commerce.signUpApply.documents.optionalLead')}
      </p>
      <p style={{ ...mutedStyle, margin: 0, fontSize: 14 }}>
        {t('commerce.trade.documentsNote', { size: megabytes(requirements.maxBytes) })}
      </p>
      {rows.map((row, index) => (
        <div key={row.key} style={rowStyle}>
          <div style={fieldStyle}>
            <label htmlFor={`upload-document-type-${row.key}`} style={labelStyle}>
              {t('commerce.trade.documentType')}
            </label>
            <select
              id={`upload-document-type-${row.key}`}
              value={row.type}
              onChange={(event) =>
                setRows((current) =>
                  current.map((item) =>
                    item.key === row.key ? { ...item, type: event.target.value as TradeDocumentType | '' } : item,
                  ),
                )
              }
              style={inputStyle}
            >
              <option value="">{t('commerce.trade.chooseDocumentType')}</option>
              {requirements.documentTypes.map((type) => (
                <option key={type} value={type}>
                  {t(`commerce.trade.documentTypes.${type}`)}
                </option>
              ))}
            </select>
          </div>
          <div style={fieldStyle}>
            <label htmlFor={`upload-document-file-${row.key}`} style={labelStyle}>
              {t('commerce.trade.documentFile', { number: index + 1 })}
            </label>
            {row.file && <span style={{ ...mutedStyle, fontSize: 13 }}>{row.file.name}</span>}
            <input
              id={`upload-document-file-${row.key}`}
              type="file"
              accept={ACCEPT}
              onChange={(event) => {
                const file = event.target.files?.[0] ?? null;
                setRows((current) => current.map((item) => (item.key === row.key ? { ...item, file } : item)));
              }}
            />
          </div>
        </div>
      ))}
      {rows.length < requirements.maxDocuments && (
        <button
          type="button"
          style={{ ...secondaryButtonStyle, justifySelf: 'start' }}
          onClick={() =>
            setRows((current) => [...current, { key: Math.max(0, ...current.map((item) => item.key)) + 1, type: '', file: null }])
          }
        >
          {t('commerce.trade.addDocument')}
        </button>
      )}
      {error && (
        <p role="alert" style={errorStyle}>
          {error}
        </p>
      )}
      <div style={actionsStyle}>
        <button type="submit" disabled={busy} style={buttonStyle}>
          {busy ? t('commerce.signUpApply.documents.submitting') : t('commerce.signUpApply.documents.submit')}
        </button>
        {!outstanding && onSkip && (
          <button type="button" disabled={busy} onClick={onSkip} style={secondaryButtonStyle}>
            {t('commerce.signUpApply.documents.skip')}
          </button>
        )}
      </div>
      {outstanding && <p style={{ ...mutedStyle, margin: 0, fontSize: 14 }}>{t('commerce.signUpApply.documents.later')}</p>}
    </form>
  );
}

/**
 * The certificates a pending installer application still lacks, one file per policy type, as the
 * signed-in shopper. The files already chosen on the form come in as `initial`. The shop answers
 * what is still outstanding; the panel keeps asking for those until nothing is.
 */
export function InstallerCertificatesUpload({
  commerce,
  types,
  initial = {},
  onDone,
}: {
  commerce: InstallerCommerceSource;
  types: InstallerPolicyType[];
  initial?: Partial<Record<InstallerPolicyType, File>>;
  onDone: (status: InstallerApplicationStatus) => void;
}) {
  const { t } = useTranslation('site-builder-blocks');
  const [files, setFiles] = useState<Partial<Record<InstallerPolicyType, File>>>(() => ({ ...initial }));
  const [waiting, setWaiting] = useState<InstallerPolicyType[]>(types);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function upload(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!commerce.uploadInstallerCertificates || busy) return;
    const chosen = waiting.flatMap((type) => (files[type] ? [{ type, file: files[type]! }] : []));
    if (!chosen.length) {
      setError(t('commerce.signUpApply.documents.none'));
      return;
    }
    const large = chosen.find((certificate) => certificate.file.size > MAX_CERTIFICATE_BYTES);
    if (large) {
      setError(t('commerce.installer.application.errors.INVALID_FILE'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const status = await commerce.uploadInstallerCertificates(chosen);
      const still = (status?.certificatesOutstanding ?? []).filter((type) => waiting.includes(type));
      if (still.length) {
        setWaiting(still);
        setFiles({});
        setError(t('commerce.signUpApply.certificates.still', { policies: still.map(policyName).join(', ') }));
        setBusy(false);
        return;
      }
      onDone(status);
    } catch (failure) {
      setError(refusalMessage(failure) ?? t(`commerce.installer.application.errors.${uploadErrorKind(failure)}`));
      setBusy(false);
    }
  }

  function policyName(type: InstallerPolicyType): string {
    return t(`commerce.installer.policies.${type}`);
  }

  return (
    <form onSubmit={(event) => void upload(event)} noValidate style={{ ...formStyle, maxWidth: 560 }}>
      <h2 style={legendStyle}>{t('commerce.signUpApply.certificates.title')}</h2>
      <p style={{ ...mutedStyle, margin: 0 }}>{t('commerce.signUpApply.certificates.lead')}</p>
      {waiting.map((type) => (
        <div key={type} style={fieldStyle}>
          <label htmlFor={`upload-certificate-${type}`} style={labelStyle}>
            {t('commerce.signUpApply.certificates.file', { policy: policyName(type) })}
          </label>
          {files[type] && <span style={{ ...mutedStyle, fontSize: 13 }}>{files[type]!.name}</span>}
          <input
            id={`upload-certificate-${type}`}
            type="file"
            accept={CERTIFICATE_ACCEPT}
            onChange={(event) => {
              const file = event.target.files?.[0];
              setFiles((current) => {
                const next = { ...current };
                if (file) next[type] = file;
                else delete next[type];
                return next;
              });
            }}
          />
        </div>
      ))}
      {error && (
        <p role="alert" style={errorStyle}>
          {error}
        </p>
      )}
      <button type="submit" disabled={busy} style={buttonStyle}>
        {busy ? t('commerce.signUpApply.documents.submitting') : t('commerce.signUpApply.certificates.submit')}
      </button>
      <p style={{ ...mutedStyle, margin: 0, fontSize: 14 }}>{t('commerce.signUpApply.documents.later')}</p>
    </form>
  );
}

/** The upload refusals the forms have words for; anything else reads as `UNAVAILABLE`. */
function uploadErrorKind(failure: unknown): string {
  const kind = failure instanceof AccountError ? failure.kind : 'UNAVAILABLE';
  return ['INVALID_FILE', 'INVALID_INPUT', 'RATE_LIMITED', 'NOT_SIGNED_IN', 'NOT_FOUND'].includes(kind) ? kind : 'UNAVAILABLE';
}

function megabytes(bytes: number): number {
  return Math.floor(bytes / (1024 * 1024));
}

const legendStyle: CSSProperties = { margin: '0 0 8px', fontSize: 'var(--font-size-lg, 18px)', fontWeight: 600 };
const sectionStyle: CSSProperties = { display: 'grid', gap: 12, margin: 0, padding: 0, border: 0, minWidth: 0 };
const pairStyle: CSSProperties = { display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' };
const fieldStyle: CSSProperties = { display: 'grid', gap: 4 };
const labelStyle: CSSProperties = { fontSize: 14, fontWeight: 500 };
const actionsStyle: CSSProperties = { display: 'flex', flexWrap: 'wrap', gap: 12 };
const rowStyle: CSSProperties = {
  display: 'grid',
  gap: 8,
  padding: '12px 14px',
  border: '1px solid var(--color-border, #e5e7eb)',
  borderRadius: 'var(--radius-md, 8px)',
};
